
import { supabase } from '@/integrations/supabase/client';
import { flowNodeService } from './supabase';

export interface Highlight {
  id: string;
  content: string;
  type: string;
  noteSpaceId: string;
  noteSpaceName: string;
  createdAt: string;
  nodeId: string;
}

export const highlightsService = {
  // Get all highlights across all user's note spaces
  async getAllHighlights(): Promise<Highlight[]> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      // Get all note spaces for the user
      const { data: noteSpaces, error: spacesError } = await supabase
        .from('note_spaces')
        .select('id, name')
        .eq('user_id', user.user.id);

      if (spacesError) throw spacesError;

      if (!noteSpaces || noteSpaces.length === 0) {
        return [];
      }

      // Get all highlighted nodes from all note spaces
      const spaceIds = noteSpaces.map(space => space.id);
      const { data: nodes, error: nodesError } = await supabase
        .from('flow_nodes')
        .select('id, data, note_space_id, created_at')
        .in('note_space_id', spaceIds)
        .eq('node_type', 'stickyNote');

      if (nodesError) throw nodesError;

      // Filter for highlighted nodes and format response
      const highlights: Highlight[] = [];
      
      if (nodes) {
        for (const node of nodes) {
          if (node.data && typeof node.data === 'object' && 
              (node.data as any).highlighted === true &&
              (node.data as any).content) {
            
            const noteSpace = noteSpaces.find(space => space.id === node.note_space_id);
            
            highlights.push({
              id: node.id,
              content: (node.data as any).content,
              type: (node.data as any).type || 'note',
              noteSpaceId: node.note_space_id,
              noteSpaceName: noteSpace?.name || 'Unknown',
              createdAt: node.created_at,
              nodeId: node.id
            });
          }
        }
      }

      // Sort by creation date, newest first
      return highlights.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } catch (error) {
      console.error('Error fetching highlights:', error);
      throw error;
    }
  },

  // Get highlights for a specific note space
  async getHighlightsBySpace(noteSpaceId: string): Promise<Highlight[]> {
    try {
      const { data: noteSpace } = await supabase
        .from('note_spaces')
        .select('name')
        .eq('id', noteSpaceId)
        .single();

      const { data: nodes, error } = await supabase
        .from('flow_nodes')
        .select('id, data, created_at')
        .eq('note_space_id', noteSpaceId)
        .eq('node_type', 'stickyNote');

      if (error) throw error;

      const highlights: Highlight[] = [];
      
      if (nodes) {
        for (const node of nodes) {
          if (node.data && typeof node.data === 'object' && 
              (node.data as any).highlighted === true &&
              (node.data as any).content) {
            
            highlights.push({
              id: node.id,
              content: (node.data as any).content,
              type: (node.data as any).type || 'note',
              noteSpaceId: noteSpaceId,
              noteSpaceName: noteSpace?.name || 'Unknown',
              createdAt: node.created_at,
              nodeId: node.id
            });
          }
        }
      }

      return highlights.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } catch (error) {
      console.error('Error fetching highlights for space:', error);
      throw error;
    }
  }
};
